/**
 * featured-collections-nosto — fetches Nosto recommendations for each tab
 * of `sections/ax-featured-collections.liquid` whose block has a
 * `nosto_placement` configured.
 *
 * How it integrates with the existing section
 * -------------------------------------------
 *   The section renders one `<featured-collection>` custom element per tab.
 *   `bundle.featured-collections.js` (lazy-loaded by the SectionObserver
 *   when the section approaches the viewport) defines the element as an
 *   `AxSwiper` subclass and attaches click listeners to the headings to
 *   activate/deactivate tabs (decoding the base64-encoded slides for
 *   non-first tabs on first click).
 *
 *   This script ADDS to that flow — never overrides it. For tabs marked
 *   with `data-nosto-placement="…"` (set by Liquid when the block has a
 *   non-empty `nosto_placement`), we:
 *
 *     1. Replace the SSR skeleton slides with Nosto-recommended product
 *        cards (fetched via the same Kusmi-style pattern we already use
 *        on the PDP / sidecart: `createRecommendationRequest` →
 *        parse `[data-nosto-recommendation]` → fetch each handle via
 *        `/products/{handle}?section_id=card-product-nosto`).
 *     2. Call `swiperInstance.update()` so Swiper picks up the new slides.
 *
 * Trigger
 * -------
 *   - Boot of any tab marked `active` at SSR (always the first tab) →
 *     fetch immediately.
 *   - Click on a tab heading whose corresponding `<featured-collection>`
 *     has a placement → fetch if not already done. We cache per placement
 *     in a Map so a re-click doesn't refetch.
 *
 * No dependency on `bundle.featured-collections.js` internals — we use
 * the public `swiperInstance` property the base AxSwiper exposes, with a
 * fallback `MutationObserver` to retry if Swiper init runs after us.
 */
(function () {
	if (typeof window === 'undefined') return;
	if (window.__featuredCollectionsNostoInstalled) return;
	window.__featuredCollectionsNostoInstalled = true;

	const SECTION_NAME = 'card-product-nosto';
	const RECO_SELECTOR = '[data-nosto-recommendation]';
	// Generous timeout: featured-collections-nosto.js runs at DOMContentLoaded
	// which can fire BEFORE Nosto's async `init.js` has installed
	// `window.nostojs`. 5 s covers slow 3G + cold-cache scenarios.
	const NOSTO_TIMEOUT_MS = 5000;
	const NOSTO_POLL_MS = 100;

	// Cache: placementId → Promise<HTMLElement[]> (array of card-wrapper nodes)
	const placementCache = new Map();

	function waitForNosto() {
		return new Promise((resolve, reject) => {
			let spent = 0;
			const tick = () => {
				if (typeof window.nostojs === 'function') return resolve();
				if (spent >= NOSTO_TIMEOUT_MS) return reject(new Error('Nosto load timeout'));
				spent += NOSTO_POLL_MS;
				setTimeout(tick, NOSTO_POLL_MS);
			};
			tick();
		});
	}

	function parseRecommendedProducts(html) {
		if (!html) return [];
		try {
			const tmp = document.createElement('div');
			tmp.innerHTML = html;
			const node = tmp.querySelector(RECO_SELECTOR);
			if (!node) return [];
			const parsed = JSON.parse(node.textContent);
			return parsed && Array.isArray(parsed.products) ? parsed.products : [];
		} catch (err) {
			console.error('[FeaturedCollectionsNosto] parse JSON failed:', err);
			return [];
		}
	}

	function extractHandle(url) {
		const noQuery = String(url).split('?')[0].replace(/\/+$/, '');
		const parts = noQuery.split('/');
		return parts[parts.length - 1];
	}

	async function fetchCard(product) {
		try {
			const handle = extractHandle(product.product_url);
			const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
			const url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_NAME;
			const resp = await fetch(url, { credentials: 'same-origin' });
			if (!resp.ok) { console.warn('[FeaturedCollectionsNosto] card fetch ' + resp.status + ' for handle', handle); return null; }
			const html = await resp.text();
			if (/^<!doctype/i.test(html.trim())) { console.warn('[FeaturedCollectionsNosto] card fetch returned full doc (DOCTYPE) for handle', handle); return null; }
			const tmp = document.createElement('div');
			tmp.innerHTML = html;
			const card = tmp.querySelector('.card-wrapper') || tmp.querySelector('.product-card-wrapper');
			if (!card) console.warn('[FeaturedCollectionsNosto] card fetch returned HTML without .card-wrapper for handle', handle);
			return card ? { product: product, cardEl: card } : null;
		} catch (err) {
			console.warn('[FeaturedCollectionsNosto] card fetch threw:', err);
			return null;
		}
	}

	function requestPlacement(placement, productsToShow) {
		if (placementCache.has(placement)) return placementCache.get(placement);
		const promise = (async function () {
			await waitForNosto();
			const reco = await new Promise(function (resolve) {
				window.nostojs(function (api) {
					api.createRecommendationRequest({ includeTagging: true })
						.setElements([placement])
						.load({ skipEvents: true })
						.then(function (response) {
							const rec = response && response.recommendations && response.recommendations[placement];
							if (!rec) {
								const returned = response && response.recommendations ? Object.keys(response.recommendations) : [];
								console.warn('[FeaturedCollectionsNosto] placement "' + placement + '" NOT returned by Nosto. Returned keys:', returned, '— check that this placement is configured on the active Nosto account (locale:', document.documentElement.lang || 'unknown', ').');
							}
							resolve(rec);
						})
						.catch(function (err) { console.error('[FeaturedCollectionsNosto] recommendation request failed:', err); resolve(null); });
				});
			});
			if (!reco) return { resultId: null, cards: [] };
			const resultId = reco.result_id || null;
			const products = parseRecommendedProducts(reco.html).slice(0, productsToShow);
			if (products.length === 0) {
				console.warn('[FeaturedCollectionsNosto] placement "' + placement + '" returned 0 products.');
				return { resultId: resultId, cards: [] };
			}
			const cards = (await Promise.all(products.map(fetchCard))).filter(Boolean);
			if (cards.length === 0) {
				console.warn('[FeaturedCollectionsNosto] placement "' + placement + '" returned ' + products.length + ' products but ALL card fetches failed.');
			}
			return { resultId: resultId, cards: cards };
		})();
		placementCache.set(placement, promise);
		return promise;
	}

	function hideTab(featuredCollectionEl) {
		// Hide the <featured-collection> AND its corresponding heading via
		// inline styles so we can target them without touching the theme
		// stylesheet.
		featuredCollectionEl.style.display = 'none';
		const sectionRoot = featuredCollectionEl.closest('.collections-multiple');
		if (!sectionRoot) return;
		const collections = Array.from(sectionRoot.querySelectorAll('featured-collection'));
		const i = collections.indexOf(featuredCollectionEl);
		if (i < 0) return;
		const headings = Array.from(sectionRoot.querySelectorAll('.featured-collections__headings_container .featured-collections__heading'));
		const heading = headings[i];
		if (heading) heading.style.display = 'none';

		// If the tab we just hid was the active one, promote the next
		// still-visible tab as active. Otherwise the theme CSS keeps every
		// other swiper at `height:0; opacity:0` because .active is what
		// unlocks them — and the whole section renders as blank.
		const hiddenSwiper = featuredCollectionEl.querySelector('.swiper');
		if (!hiddenSwiper || !hiddenSwiper.classList.contains('active')) return;
		let nextIndex = -1;
		for (let j = 0; j < collections.length; j++) {
			if (j === i) continue;
			if (getComputedStyle(collections[j]).display !== 'none') { nextIndex = j; break; }
		}
		if (nextIndex === -1) return;
		hiddenSwiper.classList.remove('active');
		if (heading) heading.classList.remove('active');
		const nextSwiper = collections[nextIndex].querySelector('.swiper');
		if (nextSwiper) {
			// Non-Nosto tabs are SSR-rendered with `.encoded` (base64) slides.
			// The theme's heading click handler normally decodes them when
			// the tab is opened. Since we're promoting the tab
			// programmatically we must decode too — otherwise the wrapper
			// stays literally `<div>base64...</div>` and no cards show.
			if (nextSwiper.classList.contains('encoded')) {
				const wrapper = nextSwiper.querySelector('.swiper-wrapper');
				if (wrapper) {
					try { wrapper.innerHTML = atob(wrapper.textContent.trim()); }
					catch (err) { console.warn('[FeaturedCollectionsNosto] atob failed:', err); }
				}
				nextSwiper.classList.remove('encoded');
			}
			nextSwiper.classList.add('active');
		}
		if (headings[nextIndex]) headings[nextIndex].classList.add('active');
	}

	function findSwiperInstance(el) {
		// AxSwiper-base exposes `swiperInstance` as an own property of the
		// custom element after init. Poll briefly in case init runs after
		// our async fetch resolves.
		return new Promise(function (resolve) {
			let spent = 0;
			const tick = function () {
				if (el.swiperInstance) return resolve(el.swiperInstance);
				if (spent >= 3000) return resolve(null);
				spent += 100;
				setTimeout(tick, 100);
			};
			tick();
		});
	}

	async function loadTab(el) {
		if (el.__nostoLoading || el.__nostoDone) return;
		el.__nostoLoading = true;

		try {
			const placement = el.dataset.nostoPlacement;
			const productsToShow = parseInt(el.dataset.nostoProductsToShow, 10) || 4;
			const wrapper = el.querySelector('[data-nosto-target="true"]');
			if (!wrapper) { el.__nostoLoading = false; return; }

			const result = await requestPlacement(placement, productsToShow);
			const cards = result.cards;
			if (cards.length === 0) {
				// Placement returned nothing (not configured / no products
				// eligible). Hide the whole tab AND its heading so the user
				// doesn't see an empty / skeleton tab. The active-tab logic
				// will keep working on the remaining ones because we don't
				// remove DOM order — just toggle visibility.
				hideTab(el);
				el.__nostoLoading = false;
				el.__nostoEmpty = true;
				el.setAttribute('data-nosto-empty', '');
				return;
			}

			// Replace skeleton slides with real ones. Each card is a fresh
			// DOM node, so we clone (the same products may render in multiple
			// tabs). Tag every slide with `data-nosto-product-id` so
			// bindTracking can wire click attribution.
			wrapper.innerHTML = '';
			cards.forEach(function (card, idx) {
				const slide = document.createElement('div');
				slide.className = 'swiper-slide grid__item';
				slide.id = (el.querySelector('[id^="Swiper-"]')?.id || 'Swiper-nosto') + '-' + (idx + 1);
				slide.dataset.nostoProductId = card.product.product_id;
				slide.dataset.nostoTrackingUrl = card.product.product_url;
				slide.appendChild(card.cardEl.cloneNode(true));
				wrapper.appendChild(slide);
			});

			el.__nostoResultId = result.resultId;
			bindTracking(el);

			const swiper = await findSwiperInstance(el);
			if (swiper && typeof swiper.update === 'function') {
				swiper.update();
			}

			// Mirror what the theme's tab decoding does so it doesn't try to
			// base64-decode our slides (they're real HTML, not encoded).
			const swiperEl = el.querySelector('.swiper');
			if (swiperEl) swiperEl.classList.remove('encoded');

			el.__nostoDone = true;
		} catch (err) {
			// Nosto SDK timeout / network error / etc. Log and hide the
			// tab so the section falls back to the other tabs. Without
			// this catch the rejection surfaces as "Uncaught (in promise)"
			// in the console AND leaves the skeleton stuck forever.
			console.warn('[FeaturedCollectionsNosto] loadTab failed for placement "' + el.dataset.nostoPlacement + '":', err && err.message);
			hideTab(el);
			el.__nostoEmpty = true;
			el.setAttribute('data-nosto-empty', '');
			el.setAttribute('data-nosto-empty-reason', (err && err.message) || 'unknown');
		} finally {
			el.__nostoLoading = false;
		}
	}

	// Wire click attribution on every card in a Nosto tab.
	// `attributeProductClicksInCampaign(productId, placement)` is Nosto's
	// direct click-attribution API. Nosto's SDK queues the event and
	// flushes it via beacon on next navigation (which is exactly what
	// happens when the user clicks a card link → PDP navigation).
	function bindTracking(el) {
		const placement = el.__nostoResultId || el.dataset.nostoPlacement;
		if (!placement) return;
		el.querySelectorAll('[data-nosto-product-id]').forEach(function (slide) {
			if (slide.__nostoTrackingBound) return;
			slide.__nostoTrackingBound = true;
			const productId = slide.dataset.nostoProductId;
			slide.querySelectorAll('a').forEach(function (link) {
				link.addEventListener('click', function () {
					if (typeof window.nostojs !== 'function') return;
					window.nostojs(function (api) {
						if (typeof api.attributeProductClicksInCampaign === 'function') {
							try { api.attributeProductClicksInCampaign(productId, placement); }
							catch (err) { console.warn('[FeaturedCollectionsNosto] click tracking failed:', err); }
						}
					});
				});
			});
		});
	}

	// Pre-load a non-active tab in the background once the page is idle.
	// Without this, the user clicks an unloaded Nosto tab → the CSS
	// transition (`.swiper`'s 500ms opacity/height anim) starts fading
	// in the skeleton (~310px tall), and mid-transition Nosto's fetch
	// resolves and swaps in real cards (~410px), producing a visible
	// layout jump. Loading off-screen means the swap happens while
	// `.swiper` is still `height: 0; opacity: 0` — invisible.
	function schedulePreload(el) {
		const run = function () { loadTab(el); };
		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(run, { timeout: 2500 });
		} else {
			setTimeout(run, 1500);
		}
	}

	function init() {
		const sections = document.querySelectorAll('.featured-collections featured-collection[data-nosto-placement]');
		if (!sections.length) return;

		// (1) Load the visible tab (the one with `.swiper.active`) on boot.
		// (2) For other Nosto tabs, schedule a background pre-load so the
		//     skeleton-to-real-cards swap happens BEFORE the user can click.
		sections.forEach(function (el) {
			const swiper = el.querySelector('.swiper');
			if (swiper && swiper.classList.contains('active')) {
				loadTab(el);
			} else {
				schedulePreload(el);
			}
		});

		// (3) Wire heading clicks as a safety net: if the user clicks a
		// tab before the idle pre-load has resolved, kick the fetch
		// immediately. `loadTab` is idempotent (`__nostoLoading` /
		// `__nostoDone` guards), so double-firing is safe.
		const sectionRoot = document.querySelector('.collections-multiple');
		if (!sectionRoot) return;
		const headings = sectionRoot.querySelectorAll('.featured-collections__headings_container .featured-collections__heading');
		const collections = sectionRoot.querySelectorAll('featured-collection');
		headings.forEach(function (heading, i) {
			heading.addEventListener('click', function () {
				const el = collections[i];
				if (el && el.dataset.nostoPlacement) loadTab(el);
			});
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init, { once: true });
	} else {
		init();
	}
})();
